<template>
  <div
    class="translation-settings"
    :class="`translation-settings--${mode}`"
  >
    <div
      v-if="showHeaderDescription"
      class="translation-settings__subtitle"
    >
      Manage your translation glossary and excluded terms
    </div>

    <input
      ref="importFile"
      type="file"
      accept=".xlsx,.xls,.csv"
      class="d-none"
      @change="handleImportFileChanged"
    >

    <div
      v-if="importing"
      class="translation-settings__import-progress mb-3"
    >
      <div class="d-flex align-items-center justify-content-between mb-1">
        <small class="text-muted">Importing translation settings...</small>
      </div>
      <b-progress
        animated
        value="100"
        variant="primary"
        height="8px"
      />
    </div>

    <div class="translation-settings__tabs">
      <button
        type="button"
        class="translation-settings__tab"
        :class="{ 'is-active': activeTab === 'glossary' }"
        @click="activeTab = 'glossary'"
      >
        <i class="bi bi-translate translation-settings__tab-icon" />
        {{ mode === "page" ? "Translation Glossary" : "Glossary" }}
      </button>
      <button
        type="button"
        class="translation-settings__tab"
        :class="{ 'is-active': activeTab === 'exclude' }"
        @click="activeTab = 'exclude'"
      >
        <i class="bi bi-shield-x translation-settings__tab-icon" />
        Exclude List
      </button>
    </div>

    <div v-if="activeTab === 'glossary'">
      <div class="translation-settings__hint">
        Define how specific terms should be translated for each language.
        <span v-if="mode === 'page'">These overrides are applied automatically during image translation.</span>
      </div>

      <div class="translation-settings__row">
        <div class="translation-settings__search">
          <i class="bi bi-search translation-settings__search-icon" />
          <input
            v-model.trim="glossarySearch"
            class="form-control translation-settings__input"
            placeholder="Search terms..."
          >
        </div>

        <div class="translation-settings__select-wrap">
          <select
            v-model="glossaryLanguageFilter"
            class="form-control translation-settings__input"
          >
            <option
              v-for="opt in languageFilterOptions"
              :key="opt"
              :value="opt"
            >
              {{ opt }}
            </option>
          </select>
        </div>

        <div
          v-if="mode === 'page'"
          class="translation-settings__page-actions"
        >
          <b-button
            size="sm"
            variant="outline-secondary"
            class="translation-settings__action-btn"
            @click="handleImportClick"
          >
            <i class="bi bi-upload mr-1" /> Import
          </b-button>
          <b-button
            size="sm"
            variant="outline-secondary"
            class="translation-settings__action-btn"
            @click="handleExport"
          >
            <i class="bi bi-download mr-1" /> Export
          </b-button>
        </div>
      </div>

      <div class="translation-settings__add-box translation-settings__glossary-box">
        <div class="translation-settings__row translation-settings__row--add">
          <input
            v-model.trim="glossaryDraft.source"
            class="form-control translation-settings__input"
            placeholder="Source term"
          >
          <select
            v-model="glossaryDraft.language"
            class="form-control translation-settings__input"
          >
            <option
              v-for="opt in sortedLanguageOptions"
              :key="opt"
              :value="opt"
            >
              {{ opt }}
            </option>
          </select>
          <input
            v-model.trim="glossaryDraft.translation"
            class="form-control translation-settings__input"
            placeholder="Translated term"
          >
          <button
            type="button"
            class="btn translation-settings__add-btn"
            @click="addGlossaryEntry"
          >
            <i class="bi bi-plus-lg mr-1" /> Add
          </button>
        </div>

        <div class="translation-settings__glossary-divider" />

        <div class="translation-settings__list translation-settings__list--glossary">
          <div
            v-for="row in filteredGlossary"
            :key="row.id"
            class="translation-settings__list-row"
          >
            <div class="translation-settings__cell translation-settings__cell--source">
              <template v-if="editingGlossaryId === row.id && editingGlossaryField === 'source'">
                <input
                  ref="editSourceInput"
                  v-model.trim="editingGlossaryDraft.source"
                  class="form-control translation-settings__input"
                  @keyup.enter.stop="saveGlossaryEdit"
                  @keyup.esc.stop="cancelGlossaryEdit"
                  @blur="saveGlossaryEdit"
                >
              </template>
              <template v-else>
                <button
                  type="button"
                  class="btn btn-link p-0 text-left w-100"
                  @click.stop="handleGlossaryCellClick(row, 'source')"
                >
                  {{ row.source }}
                </button>
              </template>
            </div>
            <div class="translation-settings__cell translation-settings__cell--lang">
              <template v-if="editingGlossaryId === row.id && editingGlossaryField === 'language'">
                <select
                  ref="editLanguageSelect"
                  v-model="editingGlossaryDraft.language"
                  class="form-control translation-settings__input"
                  @click.stop
                  @keyup.enter.stop="saveGlossaryEdit"
                  @keyup.esc.stop="cancelGlossaryEdit"
                  @change="saveGlossaryEdit"
                >
                  <option
                    v-for="opt in sortedLanguageOptions"
                    :key="opt"
                    :value="opt"
                  >
                    {{ opt }}
                  </option>
                </select>
              </template>
              <template v-else>
                <button
                  type="button"
                  class="btn btn-link p-0 text-left w-100"
                  @click.stop="handleGlossaryCellClick(row, 'language')"
                >
                  {{ row.language }}
                </button>
              </template>
            </div>
            <div class="translation-settings__cell translation-settings__cell--trans">
              <template v-if="editingGlossaryId === row.id && editingGlossaryField === 'translation'">
                <input
                  ref="editTranslationInput"
                  v-model.trim="editingGlossaryDraft.translation"
                  class="form-control translation-settings__input"
                  @keyup.enter.stop="saveGlossaryEdit"
                  @keyup.esc.stop="cancelGlossaryEdit"
                  @blur="saveGlossaryEdit"
                >
              </template>
              <template v-else>
                <button
                  type="button"
                  class="btn btn-link p-0 text-left w-100"
                  @click.stop="handleGlossaryCellClick(row, 'translation')"
                >
                  {{ row.translation }}
                </button>
              </template>
            </div>
            <button
              type="button"
              class="translation-settings__trash"
              title="Delete"
              @click.stop="removeGlossaryEntry(row.id)"
            >
              <i class="bi bi-trash" />
            </button>
          </div>

          <div
            v-if="filteredGlossary.length === 0"
            class="translation-settings__empty translation-settings__empty--glossary"
          >
            No glossary entries yet.
          </div>
        </div>
      </div>

  

      <div class="translation-settings__footer">
        {{ glossaryCountText }}
      </div>
    </div>

    <div v-else>
      <div class="translation-settings__hint">
        Terms that should never be translated (brand names, model numbers, etc.).
      </div>

      <div class="translation-settings__row translation-settings__row--exclude-search">
        <div class="translation-settings__search w-100">
          <i class="bi bi-search translation-settings__search-icon" />
          <input
            v-model.trim="excludeSearch"
            class="form-control translation-settings__input"
            placeholder="Search terms..."
          >
        </div>
      </div>

      <div class="translation-settings__row translation-settings__row--exclude">
        <input
          v-model.trim="excludeDraft"
          class="form-control translation-settings__input"
          placeholder="Add term to exclude (e.g. brand name, model number)"
          @keyup.enter="addExcludeTerm"
        >
        <button
          type="button"
          class="btn translation-settings__add-btn"
          @click="addExcludeTerm"
        >
          <i class="bi bi-plus-lg mr-1" /> Add
        </button>
        <b-button
          v-if="mode === 'page'"
          size="sm"
          variant="outline-secondary"
          class="translation-settings__action-btn"
          @click="handleImportClick"
        >
          <i class="bi bi-upload mr-1" /> Import
        </b-button>
        <b-button
          v-if="mode === 'page'"
          size="sm"
          variant="outline-secondary"
          class="translation-settings__action-btn"
          @click="handleExport"
        >
          <i class="bi bi-download mr-1" /> Export
        </b-button>
      </div>

      <div class="translation-settings__exclude-box">
        <div
          v-for="term in filteredExclude"
          :key="term"
          class="translation-settings__exclude-row"
        >
          <div class="translation-settings__exclude-term">
            <template v-if="editingExcludeTerm === term">
              <input
                ref="excludeEditInput"
                v-model.trim="editingExcludeDraft"
                class="form-control translation-settings__input translation-settings__exclude-input"
                @keyup.enter.stop="saveExcludeEdit"
                @keyup.esc.stop="cancelExcludeEdit"
                @blur="saveExcludeEdit"
              >
            </template>
            <template v-else>
              <button
                type="button"
                class="btn btn-link p-0 text-left w-100 translation-settings__exclude-labelbtn"
                @click.stop="startExcludeEdit(term)"
              >
                {{ term }}
              </button>
            </template>
          </div>
          <button
            type="button"
            class="translation-settings__exclude-trash"
            title="Delete"
            @click.stop="removeExcludeTerm(term)"
          >
            <i class="bi bi-trash" />
          </button>
        </div>

        <div
          v-if="filteredExclude.length === 0"
          class="translation-settings__empty translation-settings__empty--exclude"
        >
          No excluded terms
        </div>
      </div>

      <div class="translation-settings__footer">
        {{ exclude.length }} term{{ exclude.length === 1 ? '' : 's' }} will be kept in their original language during translation
      </div>
    </div>
  </div>
</template>

<script>
import {
  loadTranslationSettings,
  saveGlossaryEntry,
  deleteGlossaryEntry,
  saveExcludeTerm,
  deleteExcludeTerm,
  importTranslationSettings,
  exportGlossary,
  exportExclude,
} from "./translation-settings-storage";

function normalizeTerm(value) {
  return String(value || "").trim();
}

export default {
  name: "TranslationSettingsView",
  props: {
    mode: {
      type: String,
      default: "modal", // 'modal' | 'page'
    },
    showHeaderDescription: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      activeTab: "glossary",
      glossary: [],
      exclude: [],
      glossarySearch: "",
      glossaryLanguageFilter: "All languages",
      glossaryDraft: {
        source: "",
        language: "English",
        translation: "",
      },
      excludeDraft: "",
      excludeSearch: "",
      importing: false,
      languageOptions: [
        "English",
        "French-CA",
        "French-FR",
        "German",
        "Spanish (LatAm)",
        "Spanish (Spain/EU)",
        "Portuguese",
        "Italian",
        "Dutch",
        "Polish",
        "Swedish",
        "Greek",
        "Turkish",
        "Russian",
        "Arabic",
        "Hebrew",
        "Hindi",
        "Bengali",
        "Thai",
        "Vietnamese",
        "Japanese",
        "Korean",
        "Cantonese",
        "Simplified Chinese",
        "Traditional Chinese",
        "Czech",
        "Slovak"
      ],
      editingGlossaryId: null,
      editingGlossaryField: null, // 'source' | 'language' | 'translation'
      editingGlossaryDraft: {
        source: "",
        language: "English",
        translation: "",
      },
      editingExcludeTerm: null,
      editingExcludeDraft: "",
    };
  },
  computed: {
    sortedLanguageOptions() {
      return [...this.languageOptions].sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
      );
    },
    glossaryLanguagesInTable() {
      const set = new Set(
        (this.glossary || [])
          .map((r) => normalizeTerm(r?.language))
          .filter(Boolean)
      );
      return [...set].sort((a, b) =>
        String(a).localeCompare(String(b), undefined, { sensitivity: "base" })
      );
    },
    languageFilterOptions() {
      // Filter dropdown: only show languages that exist in the table
      return ["All languages", ...this.glossaryLanguagesInTable];
    },
    filteredGlossary() {
      const q = (this.glossarySearch || "").toLowerCase();
      const lang = this.glossaryLanguageFilter;

      return this.glossary.filter((row) => {
        const matchesLang = lang === "All languages" ? true : row.language === lang;
        if (!matchesLang) return false;
        if (!q) return true;
        return (
          String(row.source).toLowerCase().includes(q) ||
          String(row.translation).toLowerCase().includes(q) ||
          String(row.language).toLowerCase().includes(q)
        );
      });
    },
    filteredExclude() {
      const q = (this.excludeSearch || "").toLowerCase();
      if (!q) return this.exclude;
      return this.exclude.filter((term) =>
        String(term).toLowerCase().includes(q)
      );
    },
    glossaryCountText() {
      const entries = this.filteredGlossary.length;
      const languages = new Set(this.filteredGlossary.map((r) => r.language));
      return `${entries} entr${entries === 1 ? "y" : "ies"} across ${languages.size} language${languages.size === 1 ? "" : "s"}`;
    },
  },
  async mounted() {
    const initial = await loadTranslationSettings();
    this.glossary = (initial.glossary || []).map((row) => ({
      id: row.id,
      source: row.source || "",
      language: row.language || "English",
      translation: row.translation || "",
    }));
    this.exclude = (initial.exclude || []).map((t) => normalizeTerm(t)).filter(Boolean);
    if (
      this.glossaryLanguageFilter !== "All languages" &&
      !this.glossaryLanguagesInTable.includes(this.glossaryLanguageFilter)
    ) {
      this.glossaryLanguageFilter = "All languages";
    }
  },
  methods: {
    emitSettingsChanged() {
      this.$emit("settings-changed", {
        glossary: this.glossary.map((row) => ({
          id: row.id,
          source: row.source || "",
          language: row.language || "English",
          translation: row.translation || "",
        })),
        exclude: this.exclude.map((term) => normalizeTerm(term)).filter(Boolean),
      });
    },
    handleGlossaryCellClick(row, field) {
      if (!row || !row.id) return;

      const nextField = field || "source";

      // If already editing this exact cell, do nothing
      if (this.editingGlossaryId === row.id && this.editingGlossaryField === nextField) {
        return;
      }

      // Save pending edits when switching away from another row or field
      if (this.editingGlossaryId && (this.editingGlossaryId !== row.id || this.editingGlossaryField !== nextField)) {
        this.saveGlossaryEdit();
      }

      this.startGlossaryEdit(row, nextField);
    },
    startGlossaryEdit(row, field = "source") {
      if (!row || !row.id) return;
      this.editingGlossaryId = row.id;
      this.editingGlossaryField = field;
      this.editingGlossaryDraft = {
        source: row.source || "",
        language: row.language || "English",
        translation: row.translation || "",
      };
      this.$nextTick(() => {
        let el;
        if (field === "source") {
          el = this.$refs.editSourceInput;
        } else if (field === "language") {
          el = this.$refs.editLanguageSelect;
        } else {
          el = this.$refs.editTranslationInput;
        }

        if (Array.isArray(el)) {
          el = el[0];
        }

        if (el && el.focus) {
          el.focus();
          if (el.select) {
            el.select();
          }
        }
      });
    },
    cancelGlossaryEdit() {
      this.editingGlossaryId = null;
      this.editingGlossaryField = null;
    },
    async saveGlossaryEdit() {
      if (!this.editingGlossaryId) return;

      const source = normalizeTerm(this.editingGlossaryDraft.source);
      const language = normalizeTerm(this.editingGlossaryDraft.language) || "English";
      const translation = normalizeTerm(this.editingGlossaryDraft.translation);

      if (!source || !translation) {
        this.cancelGlossaryEdit();
        return;
      }

      try {
        const updated = await saveGlossaryEntry({
          id: this.editingGlossaryId,
          source,
          language,
          translation,
        });

        this.glossary = this.glossary.map((row) =>
          row.id === this.editingGlossaryId
            ? {
                id: updated.id,
                source: updated.source || source,
                language: updated.language || language,
                translation: updated.translation || translation,
              }
            : row
        );
        this.emitSettingsChanged();
      } catch (err) {
        console.error("Failed to update glossary entry", err);
      } finally {
        this.cancelGlossaryEdit();
      }
    },
    startExcludeEdit(term) {
      const normalized = normalizeTerm(term);
      if (!normalized) return;
      this.editingExcludeTerm = normalized;
      this.editingExcludeDraft = normalized;
      this.$nextTick(() => {
        let el = this.$refs.excludeEditInput;
        if (Array.isArray(el)) el = el[0];
        if (el && el.focus) {
          el.focus();
          if (el.select) el.select();
        }
      });
    },
    cancelExcludeEdit() {
      this.editingExcludeTerm = null;
      this.editingExcludeDraft = "";
    },
    async saveExcludeEdit() {
      const original = normalizeTerm(this.editingExcludeTerm);
      const next = normalizeTerm(this.editingExcludeDraft);

      if (!original) {
        this.cancelExcludeEdit();
        return;
      }

      // If unchanged, just exit edit mode
      if (original === next) {
        this.cancelExcludeEdit();
        return;
      }

      if (!next) {
        // Empty new value = delete
        await this.removeExcludeTerm(original);
        this.cancelExcludeEdit();
        return;
      }

      // Avoid duplicates (case-insensitive)
      const exists = this.exclude.some(
        (t) => t.toLowerCase() === next.toLowerCase()
      );
      if (exists) {
        this.cancelExcludeEdit();
        return;
      }

      try {
        // Remove old, add new
        await deleteExcludeTerm(original);
        await saveExcludeTerm(next);
        this.exclude = this.exclude
          .filter((t) => normalizeTerm(t) !== original)
          .concat([next]);
        this.emitSettingsChanged();
      } catch (err) {
        console.error("Failed to update exclude term", err);
      } finally {
        this.cancelExcludeEdit();
      }
    },
    async addGlossaryEntry() {
      const source = normalizeTerm(this.glossaryDraft.source);
      const language = normalizeTerm(this.glossaryDraft.language) || "English";
      const translation = normalizeTerm(this.glossaryDraft.translation);

      if (!source || !translation) {
        return;
      }

      try {
        const entry = await saveGlossaryEntry({
          source,
          language,
          translation,
        });

        this.glossary.push(entry);
        this.emitSettingsChanged();

        this.glossaryDraft.source = "";
        this.glossaryDraft.translation = "";
      } catch (err) {
        console.error("Failed to add glossary entry", err);
      }
    },
    async removeGlossaryEntry(id) {
      try {
        await deleteGlossaryEntry(id);
        this.glossary = this.glossary.filter((r) => r.id !== id);
        this.emitSettingsChanged();
      } catch (err) {
        console.error("Failed to remove glossary entry", err);
      }
    },
    async addExcludeTerm() {
      const term = normalizeTerm(this.excludeDraft);
      if (!term) return;

      const exists = this.exclude.some((t) => t.toLowerCase() === term.toLowerCase());
      if (exists) {
        this.excludeDraft = "";
        return;
      }

      try {
        await saveExcludeTerm(term);
        this.exclude.push(term);
        this.emitSettingsChanged();
        this.excludeDraft = "";
      } catch (err) {
        console.error("Failed to add exclude term", err);
      }
    },
    async removeExcludeTerm(term) {
      try {
        await deleteExcludeTerm(term);
        this.exclude = this.exclude.filter((t) => t !== term);
        this.emitSettingsChanged();
      } catch (err) {
        console.error("Failed to remove exclude term", err);
      }
    },
    async handleExport() {
      try {
        let response;
        let filename;

        if (this.activeTab === "glossary") {
          response = await exportGlossary(this.glossaryLanguageFilter);
          filename = `translation-glossary${this.glossaryLanguageFilter !== "All languages" ? "-" + this.glossaryLanguageFilter.toLowerCase() : ""}.xlsx`;
        } else {
          response = await exportExclude();
          filename = "exclude-list.xlsx";
        }

        const url = window.URL.createObjectURL(new Blob([response]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } catch (err) {
        console.error("Export failed", err);
        toastr.error("Export failed. Please try again.");
      }
    },
    handleImportClick() {
      if (this.$refs.importFile) {
        this.$refs.importFile.value = "";
        this.$refs.importFile.click();
      }
    },
    async handleImportFileChanged(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;

      this.importing = true;
      try {
        const response = await importTranslationSettings(file);
        
        // Refresh data
        const next = await loadTranslationSettings();
        this.glossary = (next.glossary || []).map((row) => ({
          id: row.id,
          source: row.source || "",
          language: row.language || "English",
          translation: row.translation || "",
        }));
        this.exclude = (next.exclude || []).map((t) => normalizeTerm(t)).filter(Boolean);
        this.emitSettingsChanged();
        toastr.success(response.message || "Settings imported successfully.");
      } catch (err) {
        console.error("Import failed", err);
        const msg = err.response?.data?.message || "Import failed. Please upload a valid XLSX file.";
        toastr.error(msg);
      } finally {
        this.importing = false;
        if (this.$refs.importFile) {
          this.$refs.importFile.value = "";
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.translation-settings__subtitle {
  color: #6b7280;
  font-size: 14px;
  margin-bottom: 14px;
}

.translation-settings__tabs {
  display: inline-flex;
  gap: 0;
  padding: 4px;
  border-radius: 6px;
  background: #eef1f5;
  margin-bottom: 16px;
  height: 40px;
}

.translation-settings__tab {
  border: 0;
  background: transparent;
  height: 32px;
  padding: 0 16px;
  border-radius: 4px;
  color: #64748b;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
  font-size: 14px !important;
}

.translation-settings__tab i{
  line-height: 32px;
}

.translation-settings__tab.is-active {
  background: #ffffff;
  color: #111827;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.06);
}

.translation-settings__tab-icon {
  font-size: 16px;
}

.translation-settings__hint {
  color: #6b7280;
  margin-bottom: 6px;
  font-size: 15px;
}

.translation-settings__row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 14px;
}

.translation-settings__row--add > * {
  flex: 1 1 0;
}

.translation-settings__row--add .translation-settings__add-btn {
  flex: 0 0 auto;
}

.translation-settings__row--exclude .translation-settings__input {
  flex: 1 1 auto;
}

.translation-settings__row--exclude .translation-settings__add-btn,
.translation-settings__row--exclude .translation-settings__action-btn {
  flex: 0 0 auto;
}

.translation-settings__search {
  position: relative;
  flex: 1 1 auto;
}

.translation-settings__search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: #9ca3af;
}

.translation-settings__input {
  height: 36px;
  border-radius: 6px;
  border: 1px solid #e2e8f0;
  font-size: 16px;
  color: #1f2937;
  background-color: #ffffff;
}

.translation-settings__search .translation-settings__input {
  padding-left: 42px;
}

.translation-settings__select-wrap {
  flex: 0 0 180px;
}


.translation-settings__page-actions {
  display: inline-flex;
  gap: 10px;
  margin-left: auto;
}

.translation-settings__action-btn {
  height: 36px;
  border-radius: 6px;
  border-color: #d8dee8;
  color: #111827;
  font-weight: 600;
  font-size: 12px;
  line-height:20px;
  background: #fff;
}

.translation-settings__add-box {
  border: 1px solid #d8dee8;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
  margin-bottom: 12px;
}

.translation-settings__glossary-box {
  padding: 0;
  overflow: hidden;
  max-height: 360px;
  overflow-y: auto;
}

.translation-settings__glossary-box .translation-settings__row--add {
  padding: 18px;
  margin-bottom: 0;
}

.translation-settings__glossary-divider {
  height: 1px;
  background: #e3e9f1;
  width: 100%;
}

.translation-settings__add-btn {
  height: 36px;
  border-radius: 6px;
  padding: 0 16px;
  background: #59c1a3;
  color: #fff;
  border: 0;
  font-weight: 600;
  font-size: 16px;
  line-height: 36px;
}

.translation-settings__add-btn:hover {
  color: #fff;
  background: #4cb396;
}

.translation-settings__list {
  border: 1px solid #d8dee8;
  border-radius: 12px;
  overflow: hidden;
  min-height: 320px;
}

.translation-settings__list--glossary {
  border: 0;
  border-radius: 0;
  min-height: 120px;
}

.translation-settings__list-row {
  display: grid;
  grid-template-columns: 1.2fr 0.7fr 1.2fr 44px;
  align-items: center;
  padding: 16px 16px;
  column-gap: 16px;
  border-bottom: 1px solid #e3e9f1;
  background: #fff;
  font-size: 16px;
}

.translation-settings__list-row .translation-settings__input {
  height: 38px;
  border-radius: 999px;
  border-width: 2px;
  border-color: #0f172a;
  padding-left: 14px;
  padding-right: 14px;
  font-weight: 600;
  font-size: 15px;
}

.translation-settings__list-row .translation-settings__input:focus {
  box-shadow: 0 0 0 1px #0f172a;
}

.translation-settings__list-row:hover .translation-settings__trash,
.translation-settings__list-row:focus-within .translation-settings__trash {
  opacity: 1;
  pointer-events: auto;
}

.translation-settings__list-row:last-child {
  border-bottom: 0;
}

.translation-settings__cell--source {
  font-weight: 700;
  color: #101828;
}

.translation-settings__trash {
  border: 0;
  background: transparent;
  color: #8290a5;
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: background-color .12s ease, color .12s ease, opacity .12s ease;
}

.translation-settings__trash:hover {
  background: #f3f4f6;
  color: #6b7280;
}

.translation-settings__empty {
  text-align: center;
  color: #98a2b3;
  font-size: 14px;
}

.translation-settings__empty--glossary {
  min-height: 120px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.translation-settings__empty--exclude {
  padding: 10px 4px;
}

.translation-settings__footer {
  color: #64748b;
  font-size: 14px;
  margin: 12px 0;
}

.translation-settings__exclude-box {
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 0;
  min-height: 50px;
  background: #fff;
  overflow: hidden;
}

.translation-settings__exclude-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  border-bottom: 1px solid #f3f4f6;
}

.translation-settings__exclude-row:last-child {
  border-bottom: 0;
}

.translation-settings__exclude-term {
  flex: 1 1 auto;
  min-width: 0;
}

.translation-settings__exclude-labelbtn {
  color: #111827;
  font-weight: 600;
  font-size: 15px;
  text-decoration: none;
  padding: 0;
}

.translation-settings__exclude-labelbtn:hover {
  text-decoration: none;
  color: #111827;
}

.translation-settings__exclude-input {
  max-width: 420px;
  height: 38px;
  border-radius: 999px;
  border-width: 2px;
  border-color: #0f172a;
  padding-left: 14px;
  padding-right: 14px;
  font-weight: 600;
  font-size: 15px;
}

.translation-settings__exclude-input:focus {
  box-shadow: 0 0 0 1px #0f172a;
}

.translation-settings__exclude-trash {
  border: 0;
  background: transparent;
  color: #8290a5;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  opacity: 0;
  pointer-events: none;
  transition: background-color .12s ease, color .12s ease, opacity .12s ease;
}

.translation-settings__exclude-trash:hover {
  background: #f3f4f6;
  color: #6b7280;
}

.translation-settings__exclude-row:hover .translation-settings__exclude-trash,
.translation-settings__exclude-row:focus-within .translation-settings__exclude-trash {
  opacity: 1;
  pointer-events: auto;
}

.translation-settings--modal {
  min-height: 0;
}

</style>

